export const sum = (values: number[]) =>
	values.reduce((total, v) => total + v, 0);

/** Whole pixels, without losing one where a search converged just below. */
const floorWidth = (width: number) => Math.floor(width + 1e-6);

/**
 * Shrinks the widest columns first, all to a shared cap, so short columns keep
 * their natural width and long ones share what is left. No column goes below
 * its minimum, so the result can still exceed `budget`.
 */
export const fitWithin = (
	preferred: number[],
	minimum: number[],
	budget: number,
) => {
	const capped = (cap: number) =>
		preferred.map((p, i) => Math.min(p, Math.max(minimum[i], cap)));
	if (sum(preferred) <= budget) {
		return preferred;
	}
	let lo = 0;
	let hi = Math.max(...preferred);
	for (let step = 0; step < 50; step++) {
		const mid = (lo + hi) / 2;
		if (sum(capped(mid)) <= budget) {
			lo = mid;
		} else {
			hi = mid;
		}
	}
	return capped(lo).map(floorWidth);
};

/**
 * Widens columns by up to `spare` in total, none beyond its `maximum`, to cut
 * the lines their wrapped text takes.
 *
 * A column's line count falls roughly as its text area (lines × width) over its
 * width, and the split that minimises the total gives each column a width in
 * proportion to the square root of its area. That favours the columns that wrap
 * most without handing them all the space, keeping wrapped widths balanced.
 * Columns with no area keep their width.
 */
export const shareSpareWidth = (
	widths: number[],
	maximum: number[],
	area: number[],
	spare: number,
) => {
	const weight = area.map(Math.sqrt);
	const widen = (scale: number) =>
		widths.map((w, i) =>
			weight[i] > 0 ? Math.min(maximum[i], Math.max(w, scale * weight[i])) : w,
		);
	const target = sum(widths) + spare;
	if (spare <= 0) {
		return widths;
	}
	if (sum(widen(Number.POSITIVE_INFINITY)) <= target) {
		return widen(Number.POSITIVE_INFINITY).map(floorWidth);
	}
	let lo = 0;
	let hi = Math.max(...maximum.map((m, i) => (weight[i] > 0 ? m / weight[i] : 0)));
	for (let step = 0; step < 50; step++) {
		const mid = (lo + hi) / 2;
		if (sum(widen(mid)) <= target) {
			lo = mid;
		} else {
			hi = mid;
		}
	}
	return widen(lo).map(floorWidth);
};
